import React, { useState, useEffect } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { columns } from '../../constants/ldcChallanPdf.js'
import { CommonTable, CommonModal } from '../common'
import LDCAndChallanReaderFrom from './LDCAndChallanReaderFrom'
import {
  saveAndUpdatePremiumFeature,
  getPremiumFeatureData,
  downloadPremiumFeature,
} from '../../api/premiumFeature.js'
import { saveBlobAsFile } from '../../api/apiUtils.js'
import { sanitizeForLog } from '../../constants/index'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { z } from 'zod'

const ldcSchema = z.object({
  clientName: z.string().nonempty('Entity Name is required'),
  enableLdcChallan: z.boolean({
    required_error: 'Enable Gen AI is required',
    invalid_type_error: 'Enable Gen AI must be a boolean',
  }),
})

const LDCAndChallanPdfReader = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [formData, setFormData] = useState({
    clientName: '',
    enableLdcChallan: true,
  })
  const [formErrors, setFormErrors] = useState({})
  const [formLoading, setFormLoading] = useState(false)

  const [ldcChallanData, setLdcChallanData] = useState([])
  const [loading, setLoading] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const { showSnackbar } = useSnackbar()

  const fetchTableData = async () => {
    setLoading(true)
    try {
      const res = await getPremiumFeatureData("LDC_CHALLAN_UPLOAD")
      setLdcChallanData(res.data)
      setLoading(false)
    } catch (error) {
      console.error('API Error:', sanitizeForLog(error?.message || 'Unknown error'))
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTableData()
  }, [])


  const handleEdit = data => {
    setFormData({
      ...formData,
      uuid: data.uuid,
      clientName: data.clientName,
      clientUuid: data.clientUuid,
      enableLdcChallan: data.isEnabled,
    })
    setModalOpen(true)
  }

  const handleFormSubmit = async e => {
    e.preventDefault()
    setFormLoading(true)

    try {
      // validate schema
      ldcSchema.parse(formData)
      setFormErrors({})

      const isEdit = Boolean(formData.uuid)

      const payload = {
        clientName: formData.clientName,
        clientUuid: formData.clientUuid,
        featureName: 'LDC_CHALLAN_UPLOAD',
        isEnabled: formData.enableLdcChallan,
      }

      if (isEdit) {
        payload.uuid = formData.uuid
      }

      const res = await saveAndUpdatePremiumFeature(payload)

      if (res.success) {
        fetchTableData()

        showSnackbar(
          isEdit ? 'Updated successfully' : 'Added successfully',
          'success'
        )
      }

      // reset and close modal
      setTimeout(() => {
        setFormLoading(false)
        setModalOpen(false)
        setFormData({})
      }, 500)
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors = {}
        error.issues.forEach(err => {
          fieldErrors[err.path[0]] = err.message
        })
        setFormErrors(fieldErrors)
      } else {
        console.error('API Error:', sanitizeForLog(error?.message || 'Unknown error'))
        showSnackbar('Something went wrong', 'error')
      }

      setFormLoading(false)
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setFormData([])
    setFormErrors({})
  }

  const handleNewUserInputChange = (field, value) => {
    setFormData(prev => {
      const updatedForm = { ...prev, [field]: value }

      try {
        // ✅ Validate this specific field only
        ldcSchema.pick({ [field]: true }).parse({ [field]: value })

        // ✅ If valid, clear any previous error for this field
        setFormErrors(prevErrors => {
          const { [field]: removed, ...rest } = prevErrors
          return rest
        })
      } catch (error) {
        if (error instanceof z.ZodError) {
          // ✅ Set field-specific validation message
          setFormErrors(prevErrors => ({
            ...prevErrors,
            [field]: error.errors[0].message,
          }))
        }
      }

      return updatedForm
    })
  }


  const handleDownload = async () => {
    if (ldcChallanData.length === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }

    setStartDownloading(true)

    try {
      const result = await downloadPremiumFeature('LDC_CHALLAN_UPLOAD')

      if (result?.success && result?.data) {
        saveBlobAsFile(result.data, 'ldc-challan-pdf-reader.xlsx')
        showSnackbar('Download successful', 'success')
      } else {
        showSnackbar(result?.error || 'Download failed', 'error')
      }
    } catch (error) {
      console.error('Download API Error:', sanitizeForLog(error?.message || 'Unknown error'))
      showSnackbar('Something went wrong', 'error')
    }

    setStartDownloading(false)
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          LDC & Challan Pdf Reader
        </Typography>

        {/* Right side - Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <span className="disabled:cursor-not-allowed">
              <IconButton
                onClick={handleDownload}
                disabled={loading || startDownloading}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            onClick={() => setModalOpen(true)}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTable
          columns={columns}
          data={ldcChallanData}
          loading={loading}
          onEdit={handleEdit}
          emptyMessage="No data found"
        />
      </Box>

      {/* modal */}
      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        // title={editingEntity ? 'Edit Entity' : 'Add New Entity'}
        title={'LDC & Challan Pdf Reader Configuration'}
        maxWidth="sm"
      >
        <LDCAndChallanReaderFrom
          formData={formData}
          formErrors={formErrors}
          formLoading={formLoading}
          setFormData={setFormData}
          isEdit={false}
          setFormErrors={setFormErrors}
          handleFormSubmit={handleFormSubmit}
          onInputChange={handleNewUserInputChange}
        />
      </CommonModal>
    </Box>
  )
}

export default LDCAndChallanPdfReader
